import { useEffect, useState } from 'react';
import { useOrgEmployeeRequestsApi } from '@shared/services';
import {
    useRequestStore,
    selectRequests,
    selectSelectedRequestId,
    selectIsLoading,
    selectError,
} from '../stores';
import { toLegacyRequests, toLegacyRequest, toEmployeeRequest } from '../utils';
import {
    useOrgEmployeeStore,
    selectOrgEmployee,
    selectOrgEmployeeLoading,
    selectOrgEmployeeError,
} from '@store/orgEmployee';
import { useRequestsActions } from './useRequestsActions';
import type {
    Request,
    RequestStatus,
    RequestSource,
} from '@features/requests/types';

/** Get effective orgEmployeeId */
function getEffectiveOrgEmployeeId(
    orgEmployeeId: string | null,
    orgEmployeeIdFromStore?: string,
): string | null {
    return (orgEmployeeId ?? orgEmployeeIdFromStore)?.trim() ?? null;
}

/**
 * Custom hook for managing employee requests
 * Uses the authenticated HTTP client to fetch requests directly,
 * and the store for state management.
 *
 * @param orgEmployeeId - The orgEmployeeId (used for API calls)
 * @param requestId - Optional specific request ID to fetch (for direct navigation to detail view)
 */
export function useRequests(
    orgEmployeeId: string | null,
    requestId?: string,
): {
    requests: Request[];
    selectedRequest: Request | null;
    selectedRequestId: string | null;
    isLoading: boolean;
    error: string | null;

    createRequest: (
        dto: import('@features/requests/types').CreateRequestDto,
    ) => Promise<Request>;
    updateRequest: (
        id: string,
        dto: import('@features/requests/types').UpdateRequestDto,
    ) => Promise<Request | null>;
    submitRequest: (id: string) => Promise<
        | {
              success: true;
              request: Request;
              conflicts?: import('@features/requests/types').Conflict[];
          }
        | {
              success: false;
              error: string;
              conflicts?: import('@features/requests/types').Conflict[];
          }
    >;
    cancelRequest: (id: string) => Request | null;
    selectRequest: (id: string) => Promise<void>;
    clearSelection: () => void;
    clearError: () => void;

    getRequestById: (id: string) => Request | null;
    getRequestsByStatus: (status: RequestStatus) => Request[];
    getRequestsByType: (type: RequestSource) => Request[];
} {
    const { get: getRequest, getAll: getAllRequests } =
        useOrgEmployeeRequestsApi();

    // Store state
    const requests = useRequestStore(selectRequests);
    const selectedRequestId = useRequestStore(selectSelectedRequestId);
    const isLoading = useRequestStore(selectIsLoading);
    const error = useRequestStore(selectError);

    // OrgEmployee state
    const orgEmployee = useOrgEmployeeStore(selectOrgEmployee);
    const orgEmployeeLoading = useOrgEmployeeStore(selectOrgEmployeeLoading);
    const orgEmployeeError = useOrgEmployeeStore(selectOrgEmployeeError);

    // Local loading state
    const [isFetchingRequest, setIsFetchingRequest] = useState(false);

    // Use actions hook
    const {
        createRequest,
        updateRequest,
        submitRequest,
        cancelRequest,
        selectRequest,
        clearSelection,
        clearError,
        getRequestById,
        setRequests,
    } = useRequestsActions(orgEmployeeId);

    // Selected request
    const selectedRequest = selectedRequestId
        ? getRequestById(selectedRequestId)
        : null;

    // Fetch requests
    useEffect(() => {
        const fetchRequestsData = async (): Promise<void> => {
            const effectiveOrgEmployeeId = getEffectiveOrgEmployeeId(
                orgEmployeeId,
                orgEmployee?.id,
            );
            if (!effectiveOrgEmployeeId) return;

            setIsFetchingRequest(true);
            try {
                if (requestId) {
                    const request = await getRequest(
                        effectiveOrgEmployeeId,
                        requestId,
                    );
                    const legacyRequest = toLegacyRequest(
                        toEmployeeRequest(request),
                    );
                    setRequests([legacyRequest]);
                    selectRequest(requestId);
                } else {
                    const response = await getAllRequests(
                        effectiveOrgEmployeeId,
                    );
                    const legacyRequests = toLegacyRequests(
                        response.elements.map(toEmployeeRequest),
                    );
                    setRequests(legacyRequests);
                }
            } catch {
                // Error handled by API service
            } finally {
                setIsFetchingRequest(false);
            }
        };
        void fetchRequestsData();
    }, [
        getRequest,
        getAllRequests,
        orgEmployeeId,
        orgEmployee?.id,
        requestId,
        setRequests,
        selectRequest,
    ]);

    // Selectors
    const getRequestsByStatus = (status: RequestStatus): Request[] =>
        requests.filter((r) => r.status === status);

    const getRequestsByType = (type: RequestSource): Request[] =>
        requests.filter((r) => r.type === type);

    return {
        requests,
        selectedRequest,
        selectedRequestId,
        isLoading: isLoading ?? orgEmployeeLoading ?? isFetchingRequest,
        error: orgEmployeeError ?? error,

        createRequest,
        updateRequest,
        submitRequest,
        cancelRequest,
        selectRequest,
        clearSelection,
        clearError,

        getRequestById,
        getRequestsByStatus,
        getRequestsByType,
    };
}
